<template>
  <FlashNotifications ref="flash" />

  <!-- Fixed-position color tooltip — never clipped by parent overflow -->
  <Teleport to="body">
    <div v-if="tooltip.visible"
      class="fixed z-[9999] pointer-events-none"
      :style="{ top: tooltip.y + 'px', left: tooltip.x + 'px' }">
      <div class="relative -translate-x-1/2 -translate-y-full -mt-2 px-2.5 py-1 rounded-lg bg-gray-900 text-white text-[11px] font-semibold whitespace-nowrap shadow-lg">
        {{ tooltip.text }}
        <span class="absolute top-full left-1/2 -translate-x-1/2 border-[5px] border-transparent border-t-gray-900"></span>
      </div>
    </div>
  </Teleport>

  <section class="bg-gray-50 dark:bg-[#0d1117]">

    <!-- ─── Header (shared, sticky) ─────────────────────── -->
    <div class="sticky top-0 z-20 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center justify-between px-4 sm:px-6 h-14 gap-2 sm:gap-4">

        <!-- Left: back + desktop breadcrumb -->
        <div class="flex items-center gap-2 sm:gap-3 min-w-0">
          <button type="button" @click="goBack"
             class="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors shrink-0">
            <svg class="w-4 h-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
            </svg>
            Events
          </button>
          <!-- Desktop breadcrumb only -->
          <div class="hidden sm:flex items-center gap-2 min-w-0">
            <div class="h-4 w-px bg-gray-200 dark:bg-gray-700 shrink-0"></div>
            <div class="flex items-center gap-2 min-w-0">
              <div class="w-2.5 h-2.5 rounded-full shrink-0 transition-colors duration-300"
                   :class="event.color || 'bg-gray-300 dark:bg-gray-600'"></div>
              <p class="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{{ headerText }}</p>
              <span class="text-gray-300 dark:text-gray-600 shrink-0">/</span>
              <p class="text-xs text-blue-600 dark:text-blue-400 font-medium truncate">{{ headerSubText }}</p>
            </div>
          </div>
        </div>

        <!-- Right: actions -->
        <div class="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <button type="button" @click="goBack"
             class="hidden sm:block px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
            {{ I18N.application.bookings.upcoming.cancel }}
          </button>
          <button type="button"
                  @click="submitEvent"
                  :disabled="!formIsValid || show_loading_in_create_event"
                  class="inline-flex items-center gap-1.5 px-3.5 sm:px-4 py-1.5 text-sm font-semibold text-white rounded-lg transition-all"
                  :class="formIsValid ? 'bg-blue-600 hover:bg-blue-700 shadow-sm' : 'bg-blue-300 dark:bg-blue-900/60 cursor-not-allowed'">
            <svg v-if="show_loading_in_create_event" class="w-3.5 h-3.5 animate-spin" viewBox="0 0 100 101" fill="none">
              <path d="M100 50.59C100 78.2 77.6 100.59 50 100.59C22.4 100.59 0 78.2 0 50.59C0 22.98 22.4.59 50 .59C77.6.59 100 22.98 100 50.59ZM9.08 50.59C9.08 73.19 27.4 91.51 50 91.51C72.6 91.51 90.92 73.19 90.92 50.59C90.92 27.99 72.6 9.67 50 9.67C27.4 9.67 9.08 27.99 9.08 50.59Z" fill="currentColor"/>
              <path d="M93.97 39.04C96.39 38.4 97.86 35.91 97.01 33.55C95.29 28.82 92.87 24.37 89.82 20.35C85.85 15.12 80.88 10.72 75.21 7.41C69.54 4.1 63.28 1.94 56.77 1.05C51.77.37 46.7.45 41.73 1.28C39.26 1.69 37.81 4.2 38.45 6.62C39.09 9.05 41.57 10.47 44.05 10.11C47.85 9.55 51.72 9.53 55.54 10.05C60.86 10.78 65.99 12.55 70.63 15.26C75.27 17.96 79.33 21.56 82.58 25.84C84.92 28.91 86.8 32.29 88.18 35.88C89.08 38.22 91.54 39.68 93.97 39.04Z" fill="#1C64F2"/>
            </svg>
            {{ show_loading_in_create_event ? saveBtnInProgressText : saveBtnText }}
          </button>
        </div>

      </div>
    </div>

    <!-- ─── Desktop: Loading skeleton ──────────────────── -->
    <div v-if="!ready" class="hidden sm:flex overflow-hidden animate-pulse" style="height: calc(100dvh - 56px)">
      <div class="w-[400px] xl:w-[440px] shrink-0 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] px-6 py-6 space-y-5">
        <div class="space-y-2">
          <div class="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div class="h-9 bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
        </div>
        <div class="space-y-2">
          <div class="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div class="h-9 bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
        </div>
        <div class="border-t border-gray-100 dark:border-gray-700/60"></div>
        <div class="grid grid-cols-2 gap-3">
          <div class="space-y-2"><div class="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div><div class="h-9 bg-gray-100 dark:bg-gray-800 rounded-lg"></div></div>
          <div class="space-y-2"><div class="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div><div class="h-9 bg-gray-100 dark:bg-gray-800 rounded-lg"></div></div>
        </div>
        <div class="space-y-2">
          <div class="h-3 w-28 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div class="h-9 bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
        </div>
        <div class="border-t border-gray-100 dark:border-gray-700/60"></div>
        <div class="space-y-2">
          <div class="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div class="grid grid-cols-2 gap-2">
            <div class="h-16 bg-gray-100 dark:bg-gray-800 rounded-xl"></div>
            <div class="h-16 bg-gray-100 dark:bg-gray-800 rounded-xl"></div>
          </div>
        </div>
      </div>
      <div class="flex-1 px-8 py-6 space-y-6">
        <div class="space-y-2">
          <div class="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div class="h-28 bg-white dark:bg-[#1C2128] rounded-lg border border-gray-200 dark:border-gray-700"></div>
        </div>
        <div class="space-y-2">
          <div class="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div class="flex gap-2.5">
            <div v-for="i in 11" :key="i" class="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700"></div>
          </div>
        </div>
        <div class="border-t border-gray-100 dark:border-gray-700/60"></div>
        <div class="space-y-2">
          <div class="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div class="flex flex-wrap gap-2">
            <div v-for="i in 5" :key="i" class="h-8 w-28 bg-white dark:bg-[#1C2128] rounded-full border border-gray-200 dark:border-gray-700"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- ─── Desktop: Two-column body ────────────────────── -->
    <div v-else class="hidden sm:flex overflow-hidden" style="height: calc(100dvh - 56px)">

      <!-- Left panel: core fields -->
      <div class="w-[400px] xl:w-[440px] shrink-0 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] overflow-y-auto">
        <div class="px-6 py-6 space-y-5">

          <!-- Event name -->
          <div class="space-y-1.5">
            <div class="flex items-center justify-between">
              <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {{ I18N.application.bookings.upcoming.event_name }} <span class="text-red-400">*</span>
              </label>
              <span class="text-[11px] text-gray-400 dark:text-gray-500 tabular-nums">{{ event.name ? event.name.length : 0 }}/80</span>
            </div>
            <input ref="eventNameInput"
                   v-model="event.name"
                   maxlength="80"
                   type="text"
                   required
                   :placeholder="I18N.application.bookings.upcoming.event_name"
                   class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow">
          </div>

          <!-- Event URL -->
          <div class="space-y-1.5">
            <div class="flex items-center justify-between">
              <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {{ I18N.application.new_event.event_link }} <span class="text-red-400">*</span>
              </label>
              <p v-if="errors.url" class="text-red-500 dark:text-red-400 text-xs font-medium">{{ errors.url }}</p>
            </div>
            <div class="relative">
              <input v-model="event.url"
                     v-on:input="onUrlInput"
                     autocapitalize="false"
                     autocorrect="false"
                     maxlength="80"
                     required
                     :placeholder="I18N.application.new_event.event_link"
                     class="w-full px-3 py-2 pr-9 text-sm border rounded-lg bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
                     :class="errors.url ? 'border-red-400 dark:border-red-500' : 'border-gray-200 dark:border-gray-700'">
              <svg v-if="errors.url" class="absolute right-2.5 top-2.5 w-4 h-4 text-red-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
              </svg>
              <svg v-else-if="event.url" class="absolute right-2.5 top-2.5 w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/>
              </svg>
            </div>
            <p class="text-[11px] text-blue-600 dark:text-blue-400 font-medium truncate">{{ headerSubText }}</p>
          </div>

          <div class="border-t border-gray-100 dark:border-gray-700/60"></div>

          <!-- Duration + Buffer -->
          <div class="grid grid-cols-2 gap-3">
            <div class="space-y-1.5">
              <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {{ I18N.application.new_event.duration }} <span class="text-red-400">*</span>
              </label>
              <select v-model="event.slot_duration"
                      class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer">
                <option v-for="(value, key) in slot_durations" :key="key" :value="key">{{ value }}</option>
              </select>
            </div>
            <div class="space-y-1.5">
              <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {{ I18N.application.new_event.padding }} <span class="text-red-400">*</span>
              </label>
              <select v-model="event.padding_duration"
                      class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer">
                <option v-for="(value, key) in padding_durations" :key="key" :value="key">{{ value }}</option>
              </select>
            </div>
          </div>

          <!-- Calendar searchable picker -->
          <div class="space-y-1.5" ref="calendarDropdown">
            <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {{ I18N.application.new_event.add_to_calendar }}
            </label>

            <!-- The field itself is always the search input — one persistent
                 element, so click/focus/type are all native <input> behavior.
                 (A previous version swapped this for a separate closed-state
                 button/div and that turned out unreliable — keep this as a
                 single element.) -->
            <div class="relative">
              <div class="w-full h-[42px] flex items-center gap-2 pl-2.5 pr-3 border transition-all"
                   :class="calendarOpen
                     ? 'rounded-t-lg border-blue-500 border-b-transparent bg-blue-50/40 dark:bg-blue-900/10'
                     : 'rounded-lg border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800/80'">
                <div v-if="hasRealCalendars && selectedCalendarType === 'GoogleCalendar'"
                     class="w-6 h-6 rounded-full bg-white flex items-center justify-center shrink-0 ring-1 ring-gray-100 dark:ring-gray-700">
                  <GoogleIcon class="w-4 h-4" />
                </div>
                <div v-else-if="hasRealCalendars && selectedCalendarType === 'OutlookCalendar'"
                     class="w-6 h-6 rounded-full bg-white flex items-center justify-center shrink-0 ring-1 ring-gray-100 dark:ring-gray-700">
                  <OutlookIcon class="w-3.5 h-3.5" />
                </div>
                <div v-else-if="hasRealCalendars && selectedCalendarLabel"
                     class="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0"
                     :style="{ backgroundColor: selectedCalendarColor }">
                  {{ selectedCalendarLabel[0].toUpperCase() }}
                </div>
                <div v-else-if="!hasRealCalendars"
                     class="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center shrink-0">
                  <svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"/>
                  </svg>
                </div>

                <input ref="calendarSearchInput"
                       v-model="calendarSearch"
                       type="text"
                       autocomplete="off"
                       :readonly="!hasRealCalendars"
                       :placeholder="hasRealCalendars ? 'Search calendars...' : 'No calendar connected'"
                       @focus="handleCalendarFocus"
                       @input="openCalendarSuggestions"
                       @keydown="handleCalendarKeydown"
                       class="flex-1 min-w-0 text-sm bg-transparent border-0 text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-0">

                <svg class="w-4 h-4 text-gray-400 shrink-0 transition-transform duration-200"
                     :class="{ 'rotate-180': calendarOpen }"
                     fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m19 9-7 7-7-7"/>
                </svg>
              </div>

              <!-- Dropdown panel — fused to the field above it -->
              <Transition name="picker">
                <div v-if="calendarOpen"
                     class="absolute z-30 top-full left-0 right-0 bg-white dark:bg-[#1C2128] border border-t-0 border-blue-500 rounded-b-lg shadow-lg overflow-hidden">

                  <!-- No calendars connected — connect prompt -->
                  <template v-if="!hasRealCalendars">
                    <div class="px-5 py-6 text-center">
                      <div class="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-950/40 dark:to-indigo-900/30 flex items-center justify-center mx-auto mb-3 border border-blue-100 dark:border-blue-800/50">
                        <svg class="w-5 h-5 text-blue-500 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
                        </svg>
                      </div>
                      <p class="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">No calendars connected</p>
                      <p class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-4 max-w-[200px] mx-auto">Connect Google or Outlook to sync bookings and unlock Google Meet.</p>
                      <button type="button"
                        @click="goToConnectCalendar"
                        class="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm">
                        Connect a calendar
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"/>
                        </svg>
                      </button>
                    </div>
                  </template>

                  <!-- Options (filtered live by the field above) -->
                  <template v-else>
                    <div class="max-h-52 overflow-y-auto py-1" ref="calendarOptionsList">
                      <button v-for="(calendar, index) in filteredCalendars"
                              :key="calendar.key"
                              type="button"
                              @click="selectCalendar(calendar.key)"
                              @mouseenter="activeCalendarIndex = index"
                              class="w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors"
                              :class="index === activeCalendarIndex || selected_connected_calendar === calendar.key
                                ? 'bg-blue-50 dark:bg-blue-900/20'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-800/60'">
                        <div v-if="calendar.type === 'GoogleCalendar'"
                             class="w-7 h-7 rounded-full bg-white flex items-center justify-center shrink-0 ring-1 ring-gray-100 dark:ring-gray-700">
                          <GoogleIcon class="w-4.5 h-4.5" />
                        </div>
                        <div v-else-if="calendar.type === 'OutlookCalendar'"
                             class="w-7 h-7 rounded-full bg-white flex items-center justify-center shrink-0 ring-1 ring-gray-100 dark:ring-gray-700">
                          <OutlookIcon class="w-4 h-4" />
                        </div>
                        <div v-else
                             class="w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-bold shrink-0"
                             :style="{ backgroundColor: calendar.color_hex || '#9ca3af' }">
                          {{ (calendarPrimaryLabel(calendar)[0] || '?').toUpperCase() }}
                        </div>
                        <div class="flex-1 min-w-0">
                          <p class="text-sm font-medium truncate"
                             :class="selected_connected_calendar === calendar.key
                               ? 'text-blue-700 dark:text-blue-400'
                               : 'text-gray-800 dark:text-gray-200'">
                            {{ calendarPrimaryLabel(calendar) }}
                          </p>
                          <p v-if="calendarSecondaryLabel(calendar)"
                             class="text-[11px] text-gray-400 dark:text-gray-500 truncate mt-0.5">
                            {{ calendarSecondaryLabel(calendar) }}
                          </p>
                        </div>
                        <svg v-if="selected_connected_calendar === calendar.key"
                             class="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0"
                             fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/>
                        </svg>
                      </button>
                      <div v-if="filteredCalendars.length === 0"
                           class="px-3 py-6 text-sm text-center text-gray-400 dark:text-gray-500">
                        No calendars found
                      </div>
                    </div>
                  </template>

                </div>
              </Transition>
            </div>
          </div>

          <div class="border-t border-gray-100 dark:border-gray-700/60"></div>

          <!-- Privacy -->
          <div class="space-y-2">
            <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {{ I18N.website_content.account_settings.event.event_privacy }}
            </label>
            <div class="grid grid-cols-2 gap-2">
              <label for="privacy-public"
                     class="flex items-center gap-2.5 p-3 rounded-xl border-2 cursor-pointer transition-colors"
                     :class="event.is_public === true
                       ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/10'
                       : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'">
                <input type="radio" v-model="event.is_public" id="privacy-public" name="is_public" :value="true" class="sr-only">
                <div class="w-7 h-7 rounded-full bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center shrink-0">
                  <svg class="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" fill="currentColor" viewBox="0 0 512 512">
                    <path d="M57.7 193l9.4 16.4c8.3 14.5 21.9 25.2 38 29.8L163 255.7c17.2 4.9 29 20.6 29 38.5v39.9c0 11 6.2 21 16 25.9s16 14.9 16 25.9v39c0 15.6 14.9 26.9 29.9 22.6c16.1-4.6 28.6-17.5 32.7-33.8l2.8-11.2c4.2-16.9 15.2-31.4 30.3-40l8.1-4.6c15-8.5 24.2-24.5 24.2-41.7v-8.3c0-12.7-5.1-24.9-14.1-33.9l-3.9-3.9c-9-9-21.2-14.1-33.9-14.1H257c-11.1 0-22.1-2.9-31.8-8.4l-34.5-19.7c-4.3-2.5-7.6-6.5-9.2-11.2c-3.2-9.6 1.1-20 10.2-24.5l5.9-3c6.6-3.3 14.3-3.9 21.3-1.5l23.2 7.7c8.2 2.7 17.2-.4 21.9-7.5c4.7-7 4.2-16.3-1.2-22.8l-13.6-16.3c-10-12-9.9-29.5 .3-41.3l15.7-18.3c8.8-10.3 10.2-25 3.5-36.7l-2.4-4.2c-3.5-.2-6.9-.3-10.4-.3C163.1 48 84.4 108.9 57.7 193zM464 256c0-36.8-9.6-71.4-26.4-101.5L412 164.8c-15.7 6.3-23.8 23.8-18.5 39.8l16.9 50.7c3.5 10.4 12 18.3 22.6 20.9l29.1 7.3c1.2-9 1.8-18.2 1.8-27.5zM0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256z"/>
                  </svg>
                </div>
                <div class="min-w-0">
                  <p class="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-none">{{ I18N.website_content.account_settings.event.public }}</p>
                  <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">Anyone can book</p>
                </div>
              </label>

              <label for="privacy-private"
                     class="flex items-center gap-2.5 p-3 rounded-xl border-2 cursor-pointer transition-colors"
                     :class="event.is_public === false
                       ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/10'
                       : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'">
                <input type="radio" v-model="event.is_public" id="privacy-private" name="is_public" :value="false" class="sr-only">
                <div class="w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center shrink-0">
                  <svg class="w-3 h-3 text-gray-500 dark:text-gray-400" fill="currentColor" viewBox="0 0 448 512">
                    <path d="M144 144v48H304V144c0-44.2-35.8-80-80-80s-80 35.8-80 80zM80 192V144C80 64.5 144.5 0 224 0s144 64.5 144 144v48h16c35.3 0 64 28.7 64 64V448c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V256c0-35.3 28.7-64 64-64H80z"/>
                  </svg>
                </div>
                <div class="min-w-0">
                  <p class="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-none">{{ I18N.website_content.account_settings.event.private }}</p>
                  <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">Link only</p>
                </div>
              </label>
            </div>
          </div>

          <!-- Allow guests -->
          <div class="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <!-- Toggle row -->
            <div class="flex items-center gap-3 px-4 py-3.5 bg-white dark:bg-[#1C2128]">
              <div class="w-9 h-9 rounded-lg bg-violet-50 dark:bg-violet-900/20 flex items-center justify-center shrink-0">
                <svg class="w-[18px] h-[18px] text-violet-600 dark:text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ I18N.application.new_event.allow_guests }}</p>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Invitees can add guests to the booking</p>
              </div>
              <button type="button"
                      @click="event.enable_guest_emails = !event.enable_guest_emails"
                      :class="event.enable_guest_emails ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'"
                      class="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2">
                <span :class="event.enable_guest_emails ? 'translate-x-5' : 'translate-x-0.5'"
                      class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200" />
              </button>
            </div>

            <!-- Guest limit row -->
            <Transition name="slide">
              <div v-if="event.enable_guest_emails"
                   class="border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 px-4 py-3.5">
                <div class="flex items-center justify-between">
                  <div>
                    <p class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ I18N.application.new_event.guests_limit }}</p>
                    <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Per booking</p>
                  </div>
                  <div class="flex items-center gap-2">
                    <button type="button"
                            @click="event.guest_emails_limit = Math.max(1, (event.guest_emails_limit || 1) - 1)"
                            class="w-8 h-8 rounded-full border border-gray-200 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-base font-medium leading-none">
                      −
                    </button>
                    <span class="w-6 text-center text-sm font-semibold text-gray-900 dark:text-gray-100 tabular-nums">{{ event.guest_emails_limit || 1 }}</span>
                    <button type="button"
                            @click="event.guest_emails_limit = Math.min(10, (event.guest_emails_limit || 1) + 1)"
                            class="w-8 h-8 rounded-full border border-gray-200 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-base font-medium leading-none">
                      +
                    </button>
                  </div>
                </div>
              </div>
            </Transition>
          </div>

        </div>
      </div>

      <!-- Right panel: description, color, location -->
      <div class="flex-1 overflow-y-auto">
        <div class="px-8 py-6 space-y-6 max-w-2xl">

          <!-- Description -->
          <div class="space-y-1.5">
            <div class="flex items-center justify-between">
              <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{{ I18N.application.new_event.description }}</label>
              <span class="text-[11px] text-gray-400 dark:text-gray-500 tabular-nums">{{ event.description ? event.description.length : 0 }}/500</span>
            </div>
            <textarea v-model="event.description"
                      rows="4"
                      maxlength="500"
                      :placeholder="I18N.application.new_event.description_placeholder || I18N.application.new_event.description"
                      class="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#1C2128] text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none leading-relaxed">
            </textarea>
          </div>

          <!-- Color -->
          <div class="space-y-2.5">
            <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {{ I18N.application.new_event.event_color }} <span class="text-red-400">*</span>
            </label>
            <div class="flex flex-wrap gap-2.5">
              <button v-for="color in colorOptions" :key="color.value"
                      type="button"
                      @click="event.color = color.value"
                      @mouseenter="showTooltip($event, color.label)"
                      @mouseleave="hideTooltip"
                      class="flex items-center justify-center w-8 h-8 rounded-full transition-all duration-150 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                      :class="[color.value, event.color === color.value ? 'ring-2 ring-offset-2 ring-blue-500 dark:ring-offset-[#0d1117] scale-110' : '']">
                <svg v-if="event.color === color.value" class="w-3.5 h-3.5 fill-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d="m10 15.586-3.293-3.293-1.414 1.414L10 18.414l9.707-9.707-1.414-1.414z"/>
                </svg>
              </button>
            </div>
          </div>

          <div class="border-t border-gray-100 dark:border-gray-700/60"></div>

          <!-- Location -->
          <div class="space-y-3">
            <div class="flex items-center justify-between">
              <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{{ I18N.application.new_event.location }}</label>
              <p v-if="errors.location" class="text-xs text-red-500 dark:text-red-400 font-medium" v-html="errors.location"></p>
            </div>

            <!-- Compact location pills -->
            <div class="flex flex-wrap gap-2">

              <label for="loc-no-location"
                     class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 cursor-pointer transition-all text-sm font-medium"
                     :class="event.location.name === 'no_location'
                       ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                       : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-[#1C2128]'">
                <input type="radio" v-model="event.location.name" id="loc-no-location" name="location" value="no_location" class="sr-only">
                <svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636"/>
                </svg>
                {{ I18N.application.new_event.no_location }}
              </label>

              <label for="loc-google-meet"
                     class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 cursor-pointer transition-all text-sm font-medium"
                     :class="event.location.name === 'google_meet'
                       ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                       : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-[#1C2128]'">
                <input type="radio" v-model="event.location.name" id="loc-google-meet" name="location" value="google_meet" class="sr-only">
                <svg class="w-3.5 h-3.5 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><path d="M24 4.904v14.355c-.045.04-.032.098-.051.146-.186.485-.832.735-1.31.335-1.043-.874-2.092-1.744-3.139-2.615l-.438-.364v3.606c0 .237-.045.468-.147.682-.285.598-.759.92-1.412.975-.035 0-.07-.003-.102-.003H5.722c-.035 0-.07-.004-.102-.004V16.427c0-.036 0-.072-.003-.104l.013-.013.048.003c2.605 0 5.21 0 7.815.003.077 0 .083-.026.083-.094-.003-1.369 0-2.733 0-4.1 0-.036.003-.072.003-.105.013.008.032.01.042.02.262.224.522.451.781.68l.714.622c.278.244.56.481.838.725.195.17.39.341.59.51.236.205.473.406.71.614.24.208.476.416.713.624.247.215.493.429.74.64.108.094.22.188.345.295V7.41c.007-.01.01-.02.016-.03l.051-.04a2268.9 2268.9 0 003.515-2.926.78.78 0 01.684-.188.8.8 0 01.596.448c.038.069.057.15.086.23z" fill="#00ab47"/><path d="M5.63 16.31l-.013.013c-.023 0-.042-.004-.064-.004H0v-8.64l.006-.005.007.003c.029 0 .057.004.086.004h5.438c.025 0 .054 0 .08-.003.003.003.006.01.01.013v8.56c0 .023 0 .04.003.059z" fill="#2683fb"/><path d="M0 16.319h5.553c.022 0 .041.003.064.003 0 .036.003.072.003.104v5.59c-.035 0-.07.004-.102.004H1.754c-.067 0-.135 0-.199.004a1.417 1.417 0 01-.432-.085C.56 21.73.198 21.334.045 20.744c-.016-.063-.013-.128-.042-.186C0 19.145 0 17.732 0 16.319z" fill="#0066d9"/><path d="M5.617 1.98H17.4c.035 0 .07-.003.102-.003.74.052 1.325.54 1.507 1.25.036.134.052.27.052.407v3.635c0 .035.003.07.006.103l-.016.03a.649.649 0 00-.058.041L14.4 11.31c-.272.23-.548.459-.82.686 0-.04-.003-.074-.003-.113V7.798c0-.11 0-.11-.106-.11H5.713a.773.773 0 00-.087.006c-.003-.003-.006-.01-.01-.013 0-.026.004-.055.004-.08V2.07l-.003-.08z" fill="#feb900"/><path d="M13.58 12.014c0-.002-.004-.006-.004-.012 0-.004 0-.004.003-.008.272-.226.548-.453.82-.684 1.533-1.29 3.066-2.577 4.596-3.867a.649.649 0 01.057-.041v9.338c-.125-.106-.237-.197-.345-.294l-.74-.64c-.237-.209-.473-.417-.713-.625l-.71-.614c-.196-.169-.391-.34-.59-.51-.278-.24-.56-.48-.838-.724-.24-.208-.477-.416-.714-.624-.26-.227-.522-.45-.78-.679-.01-.006-.027-.01-.043-.016z" fill="#00832d"/><path d="M5.614 1.99l.003.082v5.53l-.003.08-.08.003H.097c-.03 0-.058-.003-.087-.003l.051-.062a5751.59 5751.59 0 005.495-5.574c.02-.02.039-.036.058-.056z" fill="#e84235"/></svg>
                Google Meet
              </label>

              <label for="loc-ask-invitee"
                     class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 cursor-pointer transition-all text-sm font-medium"
                     :class="event.location.name === 'ask_invitee'
                       ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                       : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-[#1C2128]'">
                <input type="radio" v-model="event.location.name" id="loc-ask-invitee" name="location" value="ask_invitee" class="sr-only">
                <svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z"/>
                </svg>
                {{ I18N.application.new_event.ask_invitee }}
              </label>

              <label for="loc-phone-call"
                     class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 cursor-pointer transition-all text-sm font-medium"
                     :class="event.location.name === 'phone_call'
                       ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                       : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-[#1C2128]'">
                <input type="radio" v-model="event.location.name" id="loc-phone-call" name="location" value="phone_call" class="sr-only">
                <svg class="w-3.5 h-3.5 shrink-0 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d="m20.487 17.14-4.065-3.696a1.001 1.001 0 0 0-1.391.043l-2.393 2.461c-.576-.11-1.734-.471-2.926-1.66-1.192-1.193-1.553-2.354-1.66-2.926l2.459-2.394a1 1 0 0 0 .043-1.391L6.859 3.513a1 1 0 0 0-1.391-.087l-2.17 1.861a1 1 0 0 0-.29.649c-.015.25-.301 6.172 4.291 10.766C11.305 20.707 16.323 21 17.705 21c.202 0 .326-.006.359-.008a.992.992 0 0 0 .648-.291l1.86-2.171a.997.997 0 0 0-.085-1.39z"/>
                </svg>
                {{ I18N.application.new_event.phone_call }}
              </label>

              <label for="loc-in-person"
                     class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 cursor-pointer transition-all text-sm font-medium"
                     :class="event.location.name === 'in_person'
                       ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                       : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-[#1C2128]'">
                <input type="radio" v-model="event.location.name" id="loc-in-person" name="location" value="in_person" class="sr-only">
                <svg class="w-3.5 h-3.5 shrink-0 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d="M12 2C7.589 2 4 5.589 4 9.995 3.971 16.44 11.696 21.784 12 22c0 0 8.029-5.56 8-12 0-4.411-3.589-8-8-8zm0 12c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z"/>
                </svg>
                {{ I18N.application.new_event.inperson }}
              </label>

            </div>

            <!-- Google Meet — no Google Calendar connected -->
            <Transition name="details">
              <div v-if="event.location.name === 'google_meet' && !hasActiveGoogleCalendar"
                   class="rounded-xl border border-blue-100 dark:border-blue-900/40 bg-gradient-to-br from-blue-50 to-indigo-50/40 dark:from-blue-950/20 dark:to-indigo-950/10 p-4">
                <div class="flex gap-3.5">
                  <div class="w-10 h-10 rounded-xl bg-white dark:bg-[#1C2128] shadow-sm border border-blue-100 dark:border-blue-800/50 flex items-center justify-center shrink-0">
                    <svg class="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><path d="M24 4.904v14.355c-.045.04-.032.098-.051.146-.186.485-.832.735-1.31.335-1.043-.874-2.092-1.744-3.139-2.615l-.438-.364v3.606c0 .237-.045.468-.147.682-.285.598-.759.92-1.412.975-.035 0-.07-.003-.102-.003H5.722c-.035 0-.07-.004-.102-.004V16.427c0-.036 0-.072-.003-.104l.013-.013.048.003c2.605 0 5.21 0 7.815.003.077 0 .083-.026.083-.094-.003-1.369 0-2.733 0-4.1 0-.036.003-.072.003-.105.013.008.032.01.042.02.262.224.522.451.781.68l.714.622c.278.244.56.481.838.725.195.17.39.341.59.51.236.205.473.406.71.614.24.208.476.416.713.624.247.215.493.429.74.64.108.094.22.188.345.295V7.41c.007-.01.01-.02.016-.03l.051-.04a2268.9 2268.9 0 003.515-2.926.78.78 0 01.684-.188.8.8 0 01.596.448c.038.069.057.15.086.23z" fill="#00ab47"/><path d="M5.63 16.31l-.013.013c-.023 0-.042-.004-.064-.004H0v-8.64l.006-.005.007.003c.029 0 .057.004.086.004h5.438c.025 0 .054 0 .08-.003.003.003.006.01.01.013v8.56c0 .023 0 .04.003.059z" fill="#2683fb"/><path d="M0 16.319h5.553c.022 0 .041.003.064.003 0 .036.003.072.003.104v5.59c-.035 0-.07.004-.102.004H1.754c-.067 0-.135 0-.199.004a1.417 1.417 0 01-.432-.085C.56 21.73.198 21.334.045 20.744c-.016-.063-.013-.128-.042-.186C0 19.145 0 17.732 0 16.319z" fill="#0066d9"/><path d="M5.617 1.98H17.4c.035 0 .07-.003.102-.003.74.052 1.325.54 1.507 1.25.036.134.052.27.052.407v3.635c0 .035.003.07.006.103l-.016.03a.649.649 0 00-.058.041L14.4 11.31c-.272.23-.548.459-.82.686 0-.04-.003-.074-.003-.113V7.798c0-.11 0-.11-.106-.11H5.713a.773.773 0 00-.087.006c-.003-.003-.006-.01-.01-.013 0-.026.004-.055.004-.08V2.07l-.003-.08z" fill="#feb900"/><path d="M13.58 12.014c0-.002-.004-.006-.004-.012 0-.004 0-.004.003-.008.272-.226.548-.453.82-.684 1.533-1.29 3.066-2.577 4.596-3.867a.649.649 0 01.057-.041v9.338c-.125-.106-.237-.197-.345-.294l-.74-.64c-.237-.209-.473-.417-.713-.625l-.71-.614c-.196-.169-.391-.34-.59-.51-.278-.24-.56-.48-.838-.724-.24-.208-.477-.416-.714-.624-.26-.227-.522-.45-.78-.679-.01-.006-.027-.01-.043-.016z" fill="#00832d"/><path d="M5.614 1.99l.003.082v5.53l-.003.08-.08.003H.097c-.03 0-.058-.003-.087-.003l.051-.062a5751.59 5751.59 0 005.495-5.574c.02-.02.039-.036.058-.056z" fill="#e84235"/></svg>
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-semibold text-gray-900 dark:text-gray-100">No Google Calendar connected</p>
                    <p class="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                      Google Meet requires an active Google Calendar. It looks like you don't have one connected yet.
                    </p>
                    <button type="button"
                      @click="$router.push('/connected_calendars')"
                      class="inline-flex items-center gap-1.5 mt-3 px-3.5 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm">
                      Connect Google Calendar
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </Transition>

            <!-- Phone call detail panel -->
            <Transition name="details">
              <div v-if="event.location.name === 'phone_call'"
                   class="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] overflow-hidden">

                <!-- Panel header -->
                <div class="flex items-center gap-2.5 px-4 py-3 border-b border-gray-100 dark:border-gray-700/60 bg-gray-50/60 dark:bg-gray-800/30">
                  <div class="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
                    <svg class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                      <path d="m20.487 17.14-4.065-3.696a1.001 1.001 0 0 0-1.391.043l-2.393 2.461c-.576-.11-1.734-.471-2.926-1.66-1.192-1.193-1.553-2.354-1.66-2.926l2.459-2.394a1 1 0 0 0 .043-1.391L6.859 3.513a1 1 0 0 0-1.391-.087l-2.17 1.861a1 1 0 0 0-.29.649c-.015.25-.301 6.172 4.291 10.766C11.305 20.707 16.323 21 17.705 21c.202 0 .326-.006.359-.008a.992.992 0 0 0 .648-.291l1.86-2.171a.997.997 0 0 0-.085-1.39z"/>
                    </svg>
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-xs font-semibold text-gray-800 dark:text-gray-200">Phone call</p>
                    <p class="text-[11px] text-gray-400 dark:text-gray-500">Choose who initiates the call</p>
                  </div>
                </div>

                <!-- Options -->
                <div class="p-3 space-y-2">

                  <!-- Option 1: I call invitee -->
                  <label class="flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all"
                         :class="event.location.phone_call.selected_option === 'host_will_call_invitee'
                           ? 'border-blue-500 bg-blue-50/40 dark:bg-blue-900/10'
                           : 'border-gray-100 dark:border-gray-700/80 hover:border-gray-200 dark:hover:border-gray-600 bg-gray-50/40 dark:bg-gray-800/20'">
                    <input id="host-will-call" v-model="event.location.phone_call.selected_option" name="phone_call_details" type="radio" value="host_will_call_invitee" class="sr-only">
                    <div class="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 transition-colors"
                         :class="event.location.phone_call.selected_option === 'host_will_call_invitee'
                           ? 'bg-blue-100 dark:bg-blue-900/40'
                           : 'bg-gray-100 dark:bg-gray-700/60'">
                      <svg class="w-4 h-4 transition-colors"
                           :class="event.location.phone_call.selected_option === 'host_will_call_invitee' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'"
                           fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"/>
                      </svg>
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-semibold leading-none mb-0.5"
                         :class="event.location.phone_call.selected_option === 'host_will_call_invitee' ? 'text-blue-700 dark:text-blue-400' : 'text-gray-900 dark:text-gray-200'">
                        {{ I18N.application.new_event.i_call_invitee }}
                      </p>
                      <p class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{{ parseLocale(I18N.application.new_event.i_call_invitee_description, { application_name: APPLICATION_NAME }) }}</p>
                    </div>
                    <div class="shrink-0 mt-1">
                      <div v-if="event.location.phone_call.selected_option === 'host_will_call_invitee'"
                           class="w-4 h-4 rounded-full bg-blue-600 flex items-center justify-center">
                        <svg class="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                        </svg>
                      </div>
                      <div v-else class="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600"></div>
                    </div>
                  </label>

                  <!-- Option 2: Invitee calls me -->
                  <label class="flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all"
                         :class="event.location.phone_call.selected_option === 'invitee_should_call_host'
                           ? 'border-blue-500 bg-blue-50/40 dark:bg-blue-900/10'
                           : 'border-gray-100 dark:border-gray-700/80 hover:border-gray-200 dark:hover:border-gray-600 bg-gray-50/40 dark:bg-gray-800/20'">
                    <input id="invitee-will-call" v-model="event.location.phone_call.selected_option" name="phone_call_details" type="radio" value="invitee_should_call_host" class="sr-only">
                    <div class="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 transition-colors"
                         :class="event.location.phone_call.selected_option === 'invitee_should_call_host'
                           ? 'bg-blue-100 dark:bg-blue-900/40'
                           : 'bg-gray-100 dark:bg-gray-700/60'">
                      <svg class="w-4 h-4 fill-current transition-colors"
                           :class="event.location.phone_call.selected_option === 'invitee_should_call_host' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'"
                           xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                        <path d="m20.487 17.14-4.065-3.696a1.001 1.001 0 0 0-1.391.043l-2.393 2.461c-.576-.11-1.734-.471-2.926-1.66-1.192-1.193-1.553-2.354-1.66-2.926l2.459-2.394a1 1 0 0 0 .043-1.391L6.859 3.513a1 1 0 0 0-1.391-.087l-2.17 1.861a1 1 0 0 0-.29.649c-.015.25-.301 6.172 4.291 10.766C11.305 20.707 16.323 21 17.705 21c.202 0 .326-.006.359-.008a.992.992 0 0 0 .648-.291l1.86-2.171a.997.997 0 0 0-.085-1.39z"/>
                      </svg>
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-semibold leading-none mb-0.5"
                         :class="event.location.phone_call.selected_option === 'invitee_should_call_host' ? 'text-blue-700 dark:text-blue-400' : 'text-gray-900 dark:text-gray-200'">
                        {{ I18N.application.new_event.invitee_call_me }}
                      </p>
                      <p class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{{ parseLocale(I18N.application.new_event.invitee_call_me_description, { application_name: APPLICATION_NAME }) }}</p>
                      <!-- Phone number input — shown only when this option is active -->
                      <Transition name="slide">
                        <div v-if="event.location.phone_call.selected_option === 'invitee_should_call_host'" class="mt-2.5">
                          <div class="relative w-44">
                            <svg class="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-gray-400 pointer-events-none fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                              <path d="m20.487 17.14-4.065-3.696a1.001 1.001 0 0 0-1.391.043l-2.393 2.461c-.576-.11-1.734-.471-2.926-1.66-1.192-1.193-1.553-2.354-1.66-2.926l2.459-2.394a1 1 0 0 0 .043-1.391L6.859 3.513a1 1 0 0 0-1.391-.087l-2.17 1.861a1 1 0 0 0-.29.649c-.015.25-.301 6.172 4.291 10.766C11.305 20.707 16.323 21 17.705 21c.202 0 .326-.006.359-.008a.992.992 0 0 0 .648-.291l1.86-2.171a.997.997 0 0 0-.085-1.39z"/>
                            </svg>
                            <input type="tel"
                                   v-model="event.location.phone_call.host_contact_number"
                                   placeholder="+1 555 000 0000"
                                   class="w-full pl-7 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                          </div>
                        </div>
                      </Transition>
                    </div>
                    <div class="shrink-0 mt-1">
                      <div v-if="event.location.phone_call.selected_option === 'invitee_should_call_host'"
                           class="w-4 h-4 rounded-full bg-blue-600 flex items-center justify-center">
                        <svg class="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                        </svg>
                      </div>
                      <div v-else class="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600"></div>
                    </div>
                  </label>

                </div>
              </div>
            </Transition>

            <!-- In-person detail panel -->
            <Transition name="details">
              <div v-if="event.location.name === 'in_person'"
                   class="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] overflow-hidden">
                <!-- Panel header -->
                <div class="flex items-center gap-2.5 px-4 py-3 border-b border-gray-100 dark:border-gray-700/60 bg-gray-50/60 dark:bg-gray-800/30">
                  <div class="w-7 h-7 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center shrink-0">
                    <svg class="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                      <path d="M12 2C7.589 2 4 5.589 4 9.995 3.971 16.44 11.696 21.784 12 22c0 0 8.029-5.56 8-12 0-4.411-3.589-8-8-8zm0 12c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z"/>
                    </svg>
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-xs font-semibold text-gray-800 dark:text-gray-200">In-person location</p>
                    <p class="text-[11px] text-gray-400 dark:text-gray-500">Shown to invitees after booking</p>
                  </div>
                  <span class="text-[11px] tabular-nums shrink-0"
                        :class="(event.location.in_person.details || '').length >= 230
                          ? 'text-red-400 font-semibold'
                          : 'text-gray-400 dark:text-gray-500'">
                    {{ (event.location.in_person.details || '').length }}/250
                  </span>
                </div>
                <!-- Textarea -->
                <div class="p-3">
                  <textarea v-model="event.location.in_person.details"
                            :required="event.location.name === 'in_person'"
                            rows="3"
                            maxlength="250"
                            placeholder="e.g. 123 Main Street, Suite 400, New York, NY 10001"
                            class="w-full px-3 py-2.5 text-sm border rounded-lg text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent resize-none leading-relaxed transition-colors"
                            :class="(event.location.in_person.details || '').length >= 230
                              ? 'border-red-300 dark:border-red-700 bg-red-50/30 dark:bg-red-950/10 focus:ring-red-400'
                              : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/60 focus:ring-blue-500'">
                  </textarea>
                </div>
              </div>
            </Transition>

          </div>

          <div class="border-t border-gray-100 dark:border-gray-700/60"></div>

          <!-- Questions -->
          <div class="space-y-3">
            <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Form Questions</label>
            <EventQuestions
              :model-value="event_questions_attributes"
              @update:model-value="event_questions_attributes = $event"
              :initial-items="event.active_event_questions_blueprint || []"
              :event-name="event.name" />
          </div>

        </div>
      </div>

    </div>

    <!-- ─── Mobile: single-column layout ────────────────── -->
    <div class="sm:hidden">

      <!-- Mobile skeleton -->
      <div v-if="!ready" class="animate-pulse pb-8">
        <div class="px-4 pt-6 pb-2"><div class="h-2.5 w-14 bg-gray-200 dark:bg-gray-700 rounded"></div></div>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700">
          <div class="px-4 py-4"><div class="h-9 bg-gray-100 dark:bg-gray-800 rounded-lg"></div></div>
          <div class="px-4 py-4"><div class="h-9 bg-gray-100 dark:bg-gray-800 rounded-lg"></div></div>
          <div class="px-4 py-4"><div class="h-14 bg-gray-100 dark:bg-gray-800 rounded-lg"></div></div>
        </div>
        <div class="px-4 pt-6 pb-2"><div class="h-2.5 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div></div>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700">
          <div class="flex items-center justify-between px-4 py-4">
            <div class="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div class="h-4 w-16 bg-gray-100 dark:bg-gray-800 rounded"></div>
          </div>
          <div class="flex items-center justify-between px-4 py-4">
            <div class="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div class="h-4 w-16 bg-gray-100 dark:bg-gray-800 rounded"></div>
          </div>
        </div>
        <div class="px-4 pt-6 pb-2"><div class="h-2.5 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div></div>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 h-24 bg-gray-100 dark:bg-gray-800"></div>
        <div class="px-4 pt-6 pb-2"><div class="h-2.5 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div></div>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700">
          <div class="px-4 py-4"><div class="flex gap-3"><div v-for="i in 11" :key="i" class="w-9 h-9 rounded-full bg-gray-200 dark:bg-gray-700 shrink-0"></div></div></div>
        </div>
      </div>

      <!-- Mobile loaded content -->
      <div v-else class="pb-24">

        <!-- ── Basics ── -->
        <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Basics</p>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700">
          <!-- Name -->
          <div class="px-4 py-3">
            <div class="flex items-center justify-between mb-1.5">
              <label class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                {{ I18N.application.bookings.upcoming.event_name }} <span class="text-red-400">*</span>
              </label>
              <span class="text-[11px] text-gray-400 tabular-nums">{{ (event.name || '').length }}/80</span>
            </div>
            <input v-model="event.name" maxlength="80" type="text" required
                   :placeholder="I18N.application.bookings.upcoming.event_name"
                   class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
          </div>
          <!-- URL -->
          <div class="px-4 py-3">
            <div class="flex items-center justify-between mb-1.5">
              <label class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                {{ I18N.application.new_event.event_link }} <span class="text-red-400">*</span>
              </label>
              <p v-if="errors.url" class="text-[11px] text-red-500 font-medium">{{ errors.url }}</p>
            </div>
            <div class="relative">
              <input v-model="event.url" v-on:input="onUrlInput" autocapitalize="false" autocorrect="false"
                     maxlength="80" required :placeholder="I18N.application.new_event.event_link"
                     class="w-full px-3 py-2 pr-9 text-sm border rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                     :class="errors.url ? 'border-red-400' : 'border-gray-200 dark:border-gray-600'">
              <svg v-if="errors.url" class="absolute right-2.5 top-2.5 w-4 h-4 text-red-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
              </svg>
              <svg v-else-if="event.url" class="absolute right-2.5 top-2.5 w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/>
              </svg>
            </div>
            <p class="text-[11px] text-blue-500 dark:text-blue-400 mt-1 truncate">{{ headerSubText }}</p>
          </div>
          <!-- Description -->
          <div class="px-4 py-3">
            <div class="flex items-center justify-between mb-1.5">
              <label class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">{{ I18N.application.new_event.description }}</label>
              <span class="text-[11px] text-gray-400 tabular-nums">{{ (event.description || '').length }}/500</span>
            </div>
            <textarea v-model="event.description" rows="3" maxlength="500"
                      :placeholder="I18N.application.new_event.description_placeholder || I18N.application.new_event.description"
                      class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none leading-relaxed">
            </textarea>
          </div>
        </div>

        <!-- ── Scheduling ── -->
        <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Scheduling</p>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700">
          <div class="flex items-center justify-between px-4 py-3.5">
            <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ I18N.application.new_event.duration }}</p>
            <select v-model="event.slot_duration"
                    class="text-sm text-blue-600 dark:text-blue-400 bg-transparent border-0 focus:outline-none cursor-pointer font-medium">
              <option v-for="(value, key) in slot_durations" :key="key" :value="key">{{ value }}</option>
            </select>
          </div>
          <div class="flex items-center justify-between px-4 py-3.5">
            <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ I18N.application.new_event.padding }}</p>
            <select v-model="event.padding_duration"
                    class="text-sm text-blue-600 dark:text-blue-400 bg-transparent border-0 focus:outline-none cursor-pointer font-medium">
              <option v-for="(value, key) in padding_durations" :key="key" :value="key">{{ value }}</option>
            </select>
          </div>
        </div>

        <!-- ── Calendar ── -->
        <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">{{ I18N.application.new_event.add_to_calendar }}</p>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden">
          <template v-if="!hasRealCalendars">
            <div class="flex items-center justify-between px-4 py-4">
              <div class="flex items-center gap-3">
                <div class="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center shrink-0">
                  <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"/>
                  </svg>
                </div>
                <p class="text-sm text-gray-400 dark:text-gray-500">No calendar connected</p>
              </div>
              <button type="button" @click="goToConnectCalendar"
                      class="text-sm text-blue-600 dark:text-blue-400 font-semibold">
                Connect →
              </button>
            </div>
          </template>
          <template v-else>
            <button v-for="(calendar, idx) in event.connected_calendars_for_ui.calendars"
                    :key="calendar.key"
                    type="button"
                    @click="selectCalendar(calendar.key)"
                    class="w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors"
                    :class="[
                      selected_connected_calendar === calendar.key ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800/40',
                      idx < event.connected_calendars_for_ui.calendars.length - 1 ? 'border-b border-gray-100 dark:border-gray-700' : ''
                    ]">
              <div v-if="calendar.type === 'GoogleCalendar'"
                   class="w-8 h-8 rounded-full bg-white flex items-center justify-center shrink-0 ring-1 ring-gray-100 dark:ring-gray-700">
                <GoogleIcon class="w-5 h-5" />
              </div>
              <div v-else-if="calendar.type === 'OutlookCalendar'"
                   class="w-8 h-8 rounded-full bg-white flex items-center justify-center shrink-0 ring-1 ring-gray-100 dark:ring-gray-700">
                <OutlookIcon class="w-4.5 h-4.5" />
              </div>
              <div v-else
                   class="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
                   :style="{ backgroundColor: calendar.color_hex || '#9ca3af' }">
                {{ (calendarPrimaryLabel(calendar)[0] || '?').toUpperCase() }}
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium truncate"
                   :class="selected_connected_calendar === calendar.key ? 'text-blue-700 dark:text-blue-400' : 'text-gray-800 dark:text-gray-200'">
                  {{ calendarPrimaryLabel(calendar) }}
                </p>
                <p v-if="calendarSecondaryLabel(calendar)" class="text-[11px] text-gray-400 mt-0.5 truncate">{{ calendarSecondaryLabel(calendar) }}</p>
              </div>
              <svg v-if="selected_connected_calendar === calendar.key"
                   class="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/>
              </svg>
            </button>
          </template>
        </div>

        <!-- ── Appearance ── -->
        <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Appearance</p>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700">
          <!-- Color -->
          <div class="px-4 py-4">
            <p class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">{{ I18N.application.new_event.event_color }}</p>
            <div class="flex flex-wrap gap-3">
              <button v-for="color in colorOptions" :key="color.value"
                      type="button"
                      @click="event.color = color.value"
                      class="flex items-center justify-center w-9 h-9 rounded-full transition-all duration-150 focus:outline-none"
                      :class="[color.value, event.color === color.value ? 'ring-2 ring-offset-2 ring-blue-500 dark:ring-offset-[#1C2128] scale-110' : '']">
                <svg v-if="event.color === color.value" class="w-4 h-4 fill-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d="m10 15.586-3.293-3.293-1.414 1.414L10 18.414l9.707-9.707-1.414-1.414z"/>
                </svg>
              </button>
            </div>
          </div>
          <!-- Privacy -->
          <div class="px-4 py-4">
            <p class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">{{ I18N.website_content.account_settings.event.event_privacy }}</p>
            <div class="grid grid-cols-2 gap-2">
              <label for="privacy-public-mob"
                     class="flex items-center gap-2.5 p-3 rounded-xl border-2 cursor-pointer transition-colors"
                     :class="event.is_public === true ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/10' : 'border-gray-200 dark:border-gray-700'">
                <input type="radio" v-model="event.is_public" id="privacy-public-mob" name="is_public_mob" :value="true" class="sr-only">
                <div class="w-7 h-7 rounded-full bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center shrink-0">
                  <svg class="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" fill="currentColor" viewBox="0 0 512 512">
                    <path d="M57.7 193l9.4 16.4c8.3 14.5 21.9 25.2 38 29.8L163 255.7c17.2 4.9 29 20.6 29 38.5v39.9c0 11 6.2 21 16 25.9s16 14.9 16 25.9v39c0 15.6 14.9 26.9 29.9 22.6c16.1-4.6 28.6-17.5 32.7-33.8l2.8-11.2c4.2-16.9 15.2-31.4 30.3-40l8.1-4.6c15-8.5 24.2-24.5 24.2-41.7v-8.3c0-12.7-5.1-24.9-14.1-33.9l-3.9-3.9c-9-9-21.2-14.1-33.9-14.1H257c-11.1 0-22.1-2.9-31.8-8.4l-34.5-19.7c-4.3-2.5-7.6-6.5-9.2-11.2c-3.2-9.6 1.1-20 10.2-24.5l5.9-3c6.6-3.3 14.3-3.9 21.3-1.5l23.2 7.7c8.2 2.7 17.2-.4 21.9-7.5c4.7-7 4.2-16.3-1.2-22.8l-13.6-16.3c-10-12-9.9-29.5 .3-41.3l15.7-18.3c8.8-10.3 10.2-25 3.5-36.7l-2.4-4.2c-3.5-.2-6.9-.3-10.4-.3C163.1 48 84.4 108.9 57.7 193zM464 256c0-36.8-9.6-71.4-26.4-101.5L412 164.8c-15.7 6.3-23.8 23.8-18.5 39.8l16.9 50.7c3.5 10.4 12 18.3 22.6 20.9l29.1 7.3c1.2-9 1.8-18.2 1.8-27.5zM0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256z"/>
                  </svg>
                </div>
                <div>
                  <p class="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-none">{{ I18N.website_content.account_settings.event.public }}</p>
                  <p class="text-[11px] text-gray-400 mt-0.5">Anyone can book</p>
                </div>
              </label>
              <label for="privacy-private-mob"
                     class="flex items-center gap-2.5 p-3 rounded-xl border-2 cursor-pointer transition-colors"
                     :class="event.is_public === false ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/10' : 'border-gray-200 dark:border-gray-700'">
                <input type="radio" v-model="event.is_public" id="privacy-private-mob" name="is_public_mob" :value="false" class="sr-only">
                <div class="w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center shrink-0">
                  <svg class="w-3 h-3 text-gray-500 dark:text-gray-400 fill-current" viewBox="0 0 448 512">
                    <path d="M144 144v48H304V144c0-44.2-35.8-80-80-80s-80 35.8-80 80zM80 192V144C80 64.5 144.5 0 224 0s144 64.5 144 144v48h16c35.3 0 64 28.7 64 64V448c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V256c0-35.3 28.7-64 64-64H80z"/>
                  </svg>
                </div>
                <div>
                  <p class="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-none">{{ I18N.website_content.account_settings.event.private }}</p>
                  <p class="text-[11px] text-gray-400 mt-0.5">Link only</p>
                </div>
              </label>
            </div>
          </div>
        </div>

        <!-- ── Guests ── -->
        <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Guests</p>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700">
          <div class="flex items-center gap-3 px-4 py-3.5">
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ I18N.application.new_event.allow_guests }}</p>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Invitees can add guests to the booking</p>
            </div>
            <button type="button"
                    @click="event.enable_guest_emails = !event.enable_guest_emails"
                    :class="event.enable_guest_emails ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'"
                    class="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none">
              <span :class="event.enable_guest_emails ? 'translate-x-5' : 'translate-x-0.5'"
                    class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200" />
            </button>
          </div>
          <Transition name="slide">
            <div v-if="event.enable_guest_emails" class="flex items-center justify-between px-4 py-3.5">
              <div>
                <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ I18N.application.new_event.guests_limit }}</p>
                <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Per booking</p>
              </div>
              <div class="flex items-center gap-2">
                <button type="button"
                        @click="event.guest_emails_limit = Math.max(1, (event.guest_emails_limit || 1) - 1)"
                        class="w-8 h-8 rounded-full border border-gray-200 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-base font-medium leading-none">−</button>
                <span class="w-6 text-center text-sm font-semibold text-gray-900 dark:text-gray-100 tabular-nums">{{ event.guest_emails_limit || 1 }}</span>
                <button type="button"
                        @click="event.guest_emails_limit = Math.min(10, (event.guest_emails_limit || 1) + 1)"
                        class="w-8 h-8 rounded-full border border-gray-200 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-base font-medium leading-none">+</button>
              </div>
            </div>
          </Transition>
        </div>

        <!-- ── Location ── -->
        <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">{{ I18N.application.new_event.location }}</p>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700">

          <!-- No location -->
          <button type="button" @click="event.location.name = 'no_location'"
                  class="w-full flex items-center gap-3 px-4 py-3.5 text-left active:bg-gray-50 dark:active:bg-gray-800/40 transition-colors">
            <div class="w-9 h-9 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center shrink-0">
              <svg class="w-4.5 h-4.5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636"/>
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ I18N.application.new_event.no_location }}</p>
              <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">No video link or address</p>
            </div>
            <svg v-if="event.location.name === 'no_location'" class="w-5 h-5 text-blue-500 shrink-0" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/>
            </svg>
          </button>

          <!-- Google Meet -->
          <button type="button" @click="event.location.name = 'google_meet'"
                  class="w-full flex items-center gap-3 px-4 py-3.5 text-left active:bg-gray-50 dark:active:bg-gray-800/40 transition-colors">
            <div class="w-9 h-9 rounded-xl bg-white dark:bg-gray-700 border border-gray-100 dark:border-gray-600 flex items-center justify-center shrink-0">
              <svg class="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><path d="M24 4.904v14.355c-.045.04-.032.098-.051.146-.186.485-.832.735-1.31.335-1.043-.874-2.092-1.744-3.139-2.615l-.438-.364v3.606c0 .237-.045.468-.147.682-.285.598-.759.92-1.412.975-.035 0-.07-.003-.102-.003H5.722c-.035 0-.07-.004-.102-.004V16.427c0-.036 0-.072-.003-.104l.013-.013.048.003c2.605 0 5.21 0 7.815.003.077 0 .083-.026.083-.094-.003-1.369 0-2.733 0-4.1 0-.036.003-.072.003-.105.013.008.032.01.042.02.262.224.522.451.781.68l.714.622c.278.244.56.481.838.725.195.17.39.341.59.51.236.205.473.406.71.614.24.208.476.416.713.624.247.215.493.429.74.64.108.094.22.188.345.295V7.41c.007-.01.01-.02.016-.03l.051-.04a2268.9 2268.9 0 003.515-2.926.78.78 0 01.684-.188.8.8 0 01.596.448c.038.069.057.15.086.23z" fill="#00ab47"/><path d="M5.63 16.31l-.013.013c-.023 0-.042-.004-.064-.004H0v-8.64l.006-.005.007.003c.029 0 .057.004.086.004h5.438c.025 0 .054 0 .08-.003.003.003.006.01.01.013v8.56c0 .023 0 .04.003.059z" fill="#2683fb"/><path d="M0 16.319h5.553c.022 0 .041.003.064.003 0 .036.003.072.003.104v5.59c-.035 0-.07.004-.102.004H1.754c-.067 0-.135 0-.199.004a1.417 1.417 0 01-.432-.085C.56 21.73.198 21.334.045 20.744c-.016-.063-.013-.128-.042-.186C0 19.145 0 17.732 0 16.319z" fill="#0066d9"/><path d="M5.617 1.98H17.4c.035 0 .07-.003.102-.003.74.052 1.325.54 1.507 1.25.036.134.052.27.052.407v3.635c0 .035.003.07.006.103l-.016.03a.649.649 0 00-.058.041L14.4 11.31c-.272.23-.548.459-.82.686 0-.04-.003-.074-.003-.113V7.798c0-.11 0-.11-.106-.11H5.713a.773.773 0 00-.087.006c-.003-.003-.006-.01-.01-.013 0-.026.004-.055.004-.08V2.07l-.003-.08z" fill="#feb900"/><path d="M13.58 12.014c0-.002-.004-.006-.004-.012 0-.004 0-.004.003-.008.272-.226.548-.453.82-.684 1.533-1.29 3.066-2.577 4.596-3.867a.649.649 0 01.057-.041v9.338c-.125-.106-.237-.197-.345-.294l-.74-.64c-.237-.209-.473-.417-.713-.625l-.71-.614c-.196-.169-.391-.34-.59-.51-.278-.24-.56-.48-.838-.724-.24-.208-.477-.416-.714-.624-.26-.227-.522-.45-.78-.679-.01-.006-.027-.01-.043-.016z" fill="#00832d"/><path d="M5.614 1.99l.003.082v5.53l-.003.08-.08.003H.097c-.03 0-.058-.003-.087-.003l.051-.062a5751.59 5751.59 0 005.495-5.574c.02-.02.039-.036.058-.056z" fill="#e84235"/></svg>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-900 dark:text-gray-100">Google Meet</p>
              <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Video conference link</p>
            </div>
            <svg v-if="event.location.name === 'google_meet'" class="w-5 h-5 text-blue-500 shrink-0" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/>
            </svg>
          </button>

          <!-- Ask invitee -->
          <button type="button" @click="event.location.name = 'ask_invitee'"
                  class="w-full flex items-center gap-3 px-4 py-3.5 text-left active:bg-gray-50 dark:active:bg-gray-800/40 transition-colors">
            <div class="w-9 h-9 rounded-xl bg-violet-50 dark:bg-violet-900/20 flex items-center justify-center shrink-0">
              <svg class="w-4.5 h-4.5 text-violet-500 dark:text-violet-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z"/>
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ I18N.application.new_event.ask_invitee }}</p>
              <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Let invitee choose the location</p>
            </div>
            <svg v-if="event.location.name === 'ask_invitee'" class="w-5 h-5 text-blue-500 shrink-0" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/>
            </svg>
          </button>

          <!-- Phone call -->
          <button type="button" @click="event.location.name = 'phone_call'"
                  class="w-full flex items-center gap-3 px-4 py-3.5 text-left active:bg-gray-50 dark:active:bg-gray-800/40 transition-colors">
            <div class="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center shrink-0">
              <svg class="w-4 h-4 text-blue-600 dark:text-blue-400 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <path d="m20.487 17.14-4.065-3.696a1.001 1.001 0 0 0-1.391.043l-2.393 2.461c-.576-.11-1.734-.471-2.926-1.66-1.192-1.193-1.553-2.354-1.66-2.926l2.459-2.394a1 1 0 0 0 .043-1.391L6.859 3.513a1 1 0 0 0-1.391-.087l-2.17 1.861a1 1 0 0 0-.29.649c-.015.25-.301 6.172 4.291 10.766C11.305 20.707 16.323 21 17.705 21c.202 0 .326-.006.359-.008a.992.992 0 0 0 .648-.291l1.86-2.171a.997.997 0 0 0-.085-1.39z"/>
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ I18N.application.new_event.phone_call }}</p>
              <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Call or be called by the invitee</p>
            </div>
            <svg v-if="event.location.name === 'phone_call'" class="w-5 h-5 text-blue-500 shrink-0" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/>
            </svg>
          </button>

          <!-- In person -->
          <button type="button" @click="event.location.name = 'in_person'"
                  class="w-full flex items-center gap-3 px-4 py-3.5 text-left active:bg-gray-50 dark:active:bg-gray-800/40 transition-colors">
            <div class="w-9 h-9 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center shrink-0">
              <svg class="w-4 h-4 text-emerald-600 dark:text-emerald-400 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <path d="M12 2C7.589 2 4 5.589 4 9.995 3.971 16.44 11.696 21.784 12 22c0 0 8.029-5.56 8-12 0-4.411-3.589-8-8-8zm0 12c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z"/>
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ I18N.application.new_event.inperson }}</p>
              <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Physical address or meeting place</p>
            </div>
            <svg v-if="event.location.name === 'in_person'" class="w-5 h-5 text-blue-500 shrink-0" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/>
            </svg>
          </button>

        </div>

        <!-- Location detail panels (mobile) -->
        <div class="mt-3 mx-4 space-y-3">

          <!-- Google Meet warning -->
          <Transition name="details">
            <div v-if="event.location.name === 'google_meet' && !hasActiveGoogleCalendar"
                 class="rounded-xl border border-blue-100 dark:border-blue-900/40 bg-gradient-to-br from-blue-50 to-indigo-50/40 dark:from-blue-950/20 dark:to-indigo-950/10 p-4">
              <div class="flex gap-3.5">
                <div class="w-10 h-10 rounded-xl bg-white dark:bg-[#1C2128] shadow-sm border border-blue-100 dark:border-blue-800/50 flex items-center justify-center shrink-0">
                  <svg class="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><path d="M24 4.904v14.355c-.045.04-.032.098-.051.146-.186.485-.832.735-1.31.335-1.043-.874-2.092-1.744-3.139-2.615l-.438-.364v3.606c0 .237-.045.468-.147.682-.285.598-.759.92-1.412.975-.035 0-.07-.003-.102-.003H5.722c-.035 0-.07-.004-.102-.004V16.427c0-.036 0-.072-.003-.104l.013-.013.048.003c2.605 0 5.21 0 7.815.003.077 0 .083-.026.083-.094-.003-1.369 0-2.733 0-4.1 0-.036.003-.072.003-.105.013.008.032.01.042.02.262.224.522.451.781.68l.714.622c.278.244.56.481.838.725.195.17.39.341.59.51.236.205.473.406.71.614.24.208.476.416.713.624.247.215.493.429.74.64.108.094.22.188.345.295V7.41c.007-.01.01-.02.016-.03l.051-.04a2268.9 2268.9 0 003.515-2.926.78.78 0 01.684-.188.8.8 0 01.596.448c.038.069.057.15.086.23z" fill="#00ab47"/><path d="M5.63 16.31l-.013.013c-.023 0-.042-.004-.064-.004H0v-8.64l.006-.005.007.003c.029 0 .057.004.086.004h5.438c.025 0 .054 0 .08-.003.003.003.006.01.01.013v8.56c0 .023 0 .04.003.059z" fill="#2683fb"/><path d="M0 16.319h5.553c.022 0 .041.003.064.003 0 .036.003.072.003.104v5.59c-.035 0-.07.004-.102.004H1.754c-.067 0-.135 0-.199.004a1.417 1.417 0 01-.432-.085C.56 21.73.198 21.334.045 20.744c-.016-.063-.013-.128-.042-.186C0 19.145 0 17.732 0 16.319z" fill="#0066d9"/><path d="M5.617 1.98H17.4c.035 0 .07-.003.102-.003.74.052 1.325.54 1.507 1.25.036.134.052.27.052.407v3.635c0 .035.003.07.006.103l-.016.03a.649.649 0 00-.058.041L14.4 11.31c-.272.23-.548.459-.82.686 0-.04-.003-.074-.003-.113V7.798c0-.11 0-.11-.106-.11H5.713a.773.773 0 00-.087.006c-.003-.003-.006-.01-.01-.013 0-.026.004-.055.004-.08V2.07l-.003-.08z" fill="#feb900"/><path d="M13.58 12.014c0-.002-.004-.006-.004-.012 0-.004 0-.004.003-.008.272-.226.548-.453.82-.684 1.533-1.29 3.066-2.577 4.596-3.867a.649.649 0 01.057-.041v9.338c-.125-.106-.237-.197-.345-.294l-.74-.64c-.237-.209-.473-.417-.713-.625l-.71-.614c-.196-.169-.391-.34-.59-.51-.278-.24-.56-.48-.838-.724-.24-.208-.477-.416-.714-.624-.26-.227-.522-.45-.78-.679-.01-.006-.027-.01-.043-.016z" fill="#00832d"/><path d="M5.614 1.99l.003.082v5.53l-.003.08-.08.003H.097c-.03 0-.058-.003-.087-.003l.051-.062a5751.59 5751.59 0 005.495-5.574c.02-.02.039-.036.058-.056z" fill="#e84235"/></svg>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-semibold text-gray-900 dark:text-gray-100">No Google Calendar connected</p>
                  <p class="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">Google Meet requires an active Google Calendar. It looks like you don't have one connected yet.</p>
                  <button type="button" @click="$router.push('/connected_calendars')"
                    class="inline-flex items-center gap-1.5 mt-3 px-3.5 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm">
                    Connect Google Calendar
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </Transition>

          <!-- Phone call panel -->
          <Transition name="details">
            <div v-if="event.location.name === 'phone_call'"
                 class="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] overflow-hidden">
              <div class="flex items-center gap-2.5 px-4 py-3 border-b border-gray-100 dark:border-gray-700/60 bg-gray-50/60 dark:bg-gray-800/30">
                <div class="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
                  <svg class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="m20.487 17.14-4.065-3.696a1.001 1.001 0 0 0-1.391.043l-2.393 2.461c-.576-.11-1.734-.471-2.926-1.66-1.192-1.193-1.553-2.354-1.66-2.926l2.459-2.394a1 1 0 0 0 .043-1.391L6.859 3.513a1 1 0 0 0-1.391-.087l-2.17 1.861a1 1 0 0 0-.29.649c-.015.25-.301 6.172 4.291 10.766C11.305 20.707 16.323 21 17.705 21c.202 0 .326-.006.359-.008a.992.992 0 0 0 .648-.291l1.86-2.171a.997.997 0 0 0-.085-1.39z"/>
                  </svg>
                </div>
                <div>
                  <p class="text-xs font-semibold text-gray-800 dark:text-gray-200">Phone call</p>
                  <p class="text-[11px] text-gray-400 dark:text-gray-500">Choose who initiates the call</p>
                </div>
              </div>
              <div class="p-3 space-y-2">
                <label class="flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all"
                       :class="event.location.phone_call.selected_option === 'host_will_call_invitee' ? 'border-blue-500 bg-blue-50/40 dark:bg-blue-900/10' : 'border-gray-100 dark:border-gray-700/80 bg-gray-50/40 dark:bg-gray-800/20'">
                  <input id="host-will-call-mob" v-model="event.location.phone_call.selected_option" name="phone_call_details_mob" type="radio" value="host_will_call_invitee" class="sr-only">
                  <div class="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 transition-colors"
                       :class="event.location.phone_call.selected_option === 'host_will_call_invitee' ? 'bg-blue-100 dark:bg-blue-900/40' : 'bg-gray-100 dark:bg-gray-700/60'">
                    <svg class="w-4 h-4 transition-colors" :class="event.location.phone_call.selected_option === 'host_will_call_invitee' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"/>
                    </svg>
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-semibold leading-none mb-0.5" :class="event.location.phone_call.selected_option === 'host_will_call_invitee' ? 'text-blue-700 dark:text-blue-400' : 'text-gray-900 dark:text-gray-200'">
                      {{ I18N.application.new_event.i_call_invitee }}
                    </p>
                    <p class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{{ parseLocale(I18N.application.new_event.i_call_invitee_description, { application_name: APPLICATION_NAME }) }}</p>
                  </div>
                  <div class="shrink-0 mt-1">
                    <div v-if="event.location.phone_call.selected_option === 'host_will_call_invitee'" class="w-4 h-4 rounded-full bg-blue-600 flex items-center justify-center">
                      <svg class="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>
                    </div>
                    <div v-else class="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600"></div>
                  </div>
                </label>
                <label class="flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all"
                       :class="event.location.phone_call.selected_option === 'invitee_should_call_host' ? 'border-blue-500 bg-blue-50/40 dark:bg-blue-900/10' : 'border-gray-100 dark:border-gray-700/80 bg-gray-50/40 dark:bg-gray-800/20'">
                  <input id="invitee-will-call-mob" v-model="event.location.phone_call.selected_option" name="phone_call_details_mob" type="radio" value="invitee_should_call_host" class="sr-only">
                  <div class="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 transition-colors"
                       :class="event.location.phone_call.selected_option === 'invitee_should_call_host' ? 'bg-blue-100 dark:bg-blue-900/40' : 'bg-gray-100 dark:bg-gray-700/60'">
                    <svg class="w-4 h-4 fill-current transition-colors" :class="event.location.phone_call.selected_option === 'invitee_should_call_host' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                      <path d="m20.487 17.14-4.065-3.696a1.001 1.001 0 0 0-1.391.043l-2.393 2.461c-.576-.11-1.734-.471-2.926-1.66-1.192-1.193-1.553-2.354-1.66-2.926l2.459-2.394a1 1 0 0 0 .043-1.391L6.859 3.513a1 1 0 0 0-1.391-.087l-2.17 1.861a1 1 0 0 0-.29.649c-.015.25-.301 6.172 4.291 10.766C11.305 20.707 16.323 21 17.705 21c.202 0 .326-.006.359-.008a.992.992 0 0 0 .648-.291l1.86-2.171a.997.997 0 0 0-.085-1.39z"/>
                    </svg>
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-semibold leading-none mb-0.5" :class="event.location.phone_call.selected_option === 'invitee_should_call_host' ? 'text-blue-700 dark:text-blue-400' : 'text-gray-900 dark:text-gray-200'">
                      {{ I18N.application.new_event.invitee_call_me }}
                    </p>
                    <p class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{{ parseLocale(I18N.application.new_event.invitee_call_me_description, { application_name: APPLICATION_NAME }) }}</p>
                    <Transition name="slide">
                      <div v-if="event.location.phone_call.selected_option === 'invitee_should_call_host'" class="mt-2.5">
                        <div class="relative w-44">
                          <svg class="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-gray-400 pointer-events-none fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path d="m20.487 17.14-4.065-3.696a1.001 1.001 0 0 0-1.391.043l-2.393 2.461c-.576-.11-1.734-.471-2.926-1.66-1.192-1.193-1.553-2.354-1.66-2.926l2.459-2.394a1 1 0 0 0 .043-1.391L6.859 3.513a1 1 0 0 0-1.391-.087l-2.17 1.861a1 1 0 0 0-.29.649c-.015.25-.301 6.172 4.291 10.766C11.305 20.707 16.323 21 17.705 21c.202 0 .326-.006.359-.008a.992.992 0 0 0 .648-.291l1.86-2.171a.997.997 0 0 0-.085-1.39z"/>
                          </svg>
                          <input type="tel" v-model="event.location.phone_call.host_contact_number"
                                 placeholder="+1 555 000 0000"
                                 class="w-full pl-7 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                        </div>
                      </div>
                    </Transition>
                  </div>
                  <div class="shrink-0 mt-1">
                    <div v-if="event.location.phone_call.selected_option === 'invitee_should_call_host'" class="w-4 h-4 rounded-full bg-blue-600 flex items-center justify-center">
                      <svg class="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>
                    </div>
                    <div v-else class="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600"></div>
                  </div>
                </label>
              </div>
            </div>
          </Transition>

          <!-- In-person panel -->
          <Transition name="details">
            <div v-if="event.location.name === 'in_person'"
                 class="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] overflow-hidden">
              <div class="flex items-center gap-2.5 px-4 py-3 border-b border-gray-100 dark:border-gray-700/60 bg-gray-50/60 dark:bg-gray-800/30">
                <div class="w-7 h-7 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center shrink-0">
                  <svg class="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M12 2C7.589 2 4 5.589 4 9.995 3.971 16.44 11.696 21.784 12 22c0 0 8.029-5.56 8-12 0-4.411-3.589-8-8-8zm0 12c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z"/>
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-xs font-semibold text-gray-800 dark:text-gray-200">In-person location</p>
                  <p class="text-[11px] text-gray-400 dark:text-gray-500">Shown to invitees after booking</p>
                </div>
                <span class="text-[11px] tabular-nums shrink-0"
                      :class="(event.location.in_person.details || '').length >= 230 ? 'text-red-400 font-semibold' : 'text-gray-400 dark:text-gray-500'">
                  {{ (event.location.in_person.details || '').length }}/250
                </span>
              </div>
              <div class="p-3">
                <textarea v-model="event.location.in_person.details"
                          :required="event.location.name === 'in_person'"
                          rows="3" maxlength="250"
                          placeholder="e.g. 123 Main Street, Suite 400, New York, NY 10001"
                          class="w-full px-3 py-2.5 text-sm border rounded-lg text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent resize-none leading-relaxed transition-colors"
                          :class="(event.location.in_person.details || '').length >= 230 ? 'border-red-300 dark:border-red-700 bg-red-50/30 focus:ring-red-400' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/60 focus:ring-blue-500'">
                </textarea>
              </div>
            </div>
          </Transition>

        </div>

        <!-- ── Questions ── -->
        <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Form Questions</p>
        <div class="mx-4 pb-2">
          <EventQuestions
            :model-value="event_questions_attributes"
            @update:model-value="event_questions_attributes = $event"
            :initial-items="event.active_event_questions_blueprint || []" />
        </div>

      </div>
    </div><!-- /mobile -->

  </section>
</template>

<script>
import { useGlobalStore } from 'stores/global.js'
import { useEventsStore } from 'stores/events.js'
import Constants from "controllers/vue_components/mixins/constants"
import FlashNotifications from "controllers/vue_components/flash_toast"
import RequestMixin from "controllers/vue_components/mixins/request"
import EventQuestions from './EventQuestions.vue'
import { GoogleIcon, OutlookIcon } from 'components/icons/calendar_provider_icons.js'

export default {
    name: 'NewEvent',
    props: ['current_event', 'slot_durations_json', 'padding_durations_to_json'],
    mixins: [RequestMixin, Constants],
    components: { FlashNotifications, EventQuestions, GoogleIcon, OutlookIcon },
    data() {
        return {
            ready: false,
            user: {},
            event: {},
            slot_durations: {},
            padding_durations: {},
            errors: {},
            show_loading_in_create_event: false,
            selected_connected_calendar: null,
            selected_zoom: null,
            urlManuallyEdited: false,
            tooltip: { visible: false, text: '', x: 0, y: 0 },
            calendarOpen: false,
            calendarSearch: '',
            activeCalendarIndex: -1,
            event_questions_attributes: [],
            colorOptions: [
                { value: 'tomato',    label: 'Tomato' },
                { value: 'flamingo',  label: 'Flamingo' },
                { value: 'tangerine', label: 'Tangerine' },
                { value: 'banana',    label: 'Banana' },
                { value: 'peacock',   label: 'Peacock' },
                { value: 'sage',      label: 'Sage' },
                { value: 'basil',     label: 'Basil' },
                { value: 'blueberry', label: 'Blueberry' },
                { value: 'lavender',  label: 'Lavender' },
                { value: 'grape',     label: 'Grape' },
                { value: 'graphite',  label: 'Graphite' },
            ],
        };
    },
    methods: {
        goBack() {
            this.$router.push('/events');
        },
        async submitEvent() {
            this.show_loading_in_create_event = true;

            const eventPayload = {
                name: this.event.name,
                color: this.event.color,
                description: this.event.description,
                url: this.event.url,
                slot_duration: this.event.slot_duration,
                padding_duration: this.event.padding_duration,
                location: this.event.location,
                connected_calendar_id: this.event.connected_calendar_id,
                payment_source: this.event.payment_source,
                is_public: this.event.is_public,
                enable_guest_emails: this.event.enable_guest_emails,
                guest_emails_limit: this.event.guest_emails_limit,
                enable_external_payment: this.event.enable_external_payment,
                external_payments_amount: this.event.external_payments_amount,
                external_payments_currency: this.event.external_payments_currency,
                active: this.event.active,
                event_questions_attributes: this.event_questions_attributes
                    .filter(q => q.question_id || q._destroy),
            };

            let data = { user: this.user, event: eventPayload };
            let resp = {};
            if (this.isNewEvent) {
                resp = await this.axiosRequest('POST', '/api/v1/events', data, false);
            } else {
                resp = await this.axiosRequest('PUT', `/api/v1/events/${this.event.id}`, data, false);
            }

            if (resp.js_error == false && resp.response.success) {
                const store = useEventsStore();
                if (this.isNewEvent) {
                    store.addEvent(resp.response.data);
                    store.invalidate();
                } else {
                    store.updateEvent(this.event.id, resp.response.data);
                }
                this.$router.push('/events');
            } else {
                const msg = resp.response?.message || resp.error?.data?.message || 'Something went wrong';
                this.$refs.flash.show(msg, 'alert');
                this.show_loading_in_create_event = false;
            }
        },
        slugify(text) {
            return text
                .toLowerCase()
                .replace(/\s+/g, '-')
                .replace(/[^a-z0-9-]/g, '')
                .replace(/-+/g, '-')
                .replace(/^-|-$/g, '');
        },
        onUrlInput() {
            this.urlManuallyEdited = true;
            this.checkUrlAvailability();
        },
        async checkUrlAvailability() {
            if (!this.event.url) {
                delete this.errors.url;
                return;
            }
            let res = await this.axiosRequest('POST', '/api/v1/events/validate_url_availability', { event: { url: this.event.url, id: this.event.id } }, false);
            if (res.js_error == false && res.response.success) {
                if (res.response.data.available) {
                    this.errors = {};
                } else {
                    this.errors.url = res.response.message;
                }
            }
        },
        isTextPresent(txt) {
            return txt !== null && txt !== '' && txt !== undefined;
        },
        setDefaultLocation() {
            this.event.location = this.event.event_location_data;
        },
        showTooltip(e, label) {
            const rect = e.currentTarget.getBoundingClientRect();
            this.tooltip = {
                visible: true,
                text: label.charAt(0).toUpperCase() + label.slice(1),
                x: rect.left + rect.width / 2,
                y: rect.top,
            };
        },
        hideTooltip() {
            this.tooltip.visible = false;
        },
        goToConnectCalendar() {
            this.calendarOpen = false;
            this.$router.push('/connected_calendars');
        },
        selectCalendar(key) {
            this.selected_connected_calendar = key;
            this.calendarSearch = this.selectedCalendarLabel;
            this.calendarOpen = false;
            this.activeCalendarIndex = -1;
        },
        openCalendarSuggestions() {
            this.calendarOpen = true;
        },
        handleCalendarFocus() {
            this.openCalendarSuggestions();
            this.$nextTick(() => {
                this.$refs.calendarSearchInput?.select();
            });
        },
        resetCalendarSearch() {
            this.calendarSearch = this.selectedCalendarLabel;
        },
        scrollActiveCalendarIntoView() {
            this.$nextTick(() => {
                const container = this.$refs.calendarOptionsList;
                const el = container?.children[this.activeCalendarIndex];
                el?.scrollIntoView({ block: 'nearest' });
            });
        },
        handleCalendarKeydown(e) {
            const hasOpen = this.calendarOpen && this.hasRealCalendars;
            if (hasOpen && e.key === 'ArrowDown') {
                e.preventDefault();
                if (this.filteredCalendars.length) this.activeCalendarIndex = (this.activeCalendarIndex + 1) % this.filteredCalendars.length;
                this.scrollActiveCalendarIntoView();
            } else if (hasOpen && e.key === 'ArrowUp') {
                e.preventDefault();
                if (this.filteredCalendars.length) {
                    this.activeCalendarIndex = this.activeCalendarIndex <= 0
                        ? this.filteredCalendars.length - 1
                        : this.activeCalendarIndex - 1;
                }
                this.scrollActiveCalendarIntoView();
            } else if (hasOpen && e.key === 'Escape') {
                this.calendarOpen = false;
                this.activeCalendarIndex = -1;
                this.resetCalendarSearch();
            } else if (e.key === 'Enter') {
                e.preventDefault();
                if (hasOpen && this.activeCalendarIndex >= 0 && this.filteredCalendars[this.activeCalendarIndex]) {
                    this.selectCalendar(this.filteredCalendars[this.activeCalendarIndex].key);
                } else if (hasOpen && this.filteredCalendars.length === 1) {
                    this.selectCalendar(this.filteredCalendars[0].key);
                }
            }
        },
        handleOutsideClick(e) {
            if (this.$refs.calendarDropdown && !this.$refs.calendarDropdown.contains(e.target) && this.calendarOpen) {
                this.calendarOpen = false;
                this.activeCalendarIndex = -1;
                this.resetCalendarSearch();
            }
        },
        calendarPrimaryLabel(calendar) {
            if (calendar.type === 'None') return calendar.value;
            return calendar.display_name || 'Primary';
        },
        calendarSecondaryLabel(calendar) {
            if (calendar.type === 'None') return '';
            return calendar.value;
        },
    },
    computed: {
        isNewEvent() {
            return !this.event.id;
        },
        headerText() {
            return this.isTextPresent(this.event.name)
                ? this.event.name
                : this.I18N.website_content.account_settings.what_event_is_this;
        },
        eventURL() {
            let txt = `${APPLICATION_URL}/${this.user.url}/`;
            if (this.isTextPresent(this.event.url)) txt += this.event.url;
            return txt;
        },
        headerSubText() {
            return this.isTextPresent(this.event.url)
                ? this.eventURL
                : this.I18N.website_content.account_settings.no_given_url;
        },
        formIsValid() {
            const googleMeetWithoutCalendar = this.event.location?.name === 'google_meet' && !this.hasActiveGoogleCalendar;
            return !this.errors.url &&
                this.isTextPresent(this.event.url) &&
                this.isTextPresent(this.event.name) &&
                !!this.event.slot_duration &&
                !googleMeetWithoutCalendar;
        },
        saveBtnText() {
            return this.isNewEvent
                ? this.I18N.application.events.create_event
                : this.I18N.application.events.update_event;
        },
        saveBtnInProgressText() {
            return this.isNewEvent
                ? this.I18N.notifications.events.creating
                : this.I18N.notifications.events.updating;
        },
        hasActiveGoogleCalendar() {
            return this.event.integrations?.google_meet === true;
        },
        filteredCalendars() {
            if (!this.event.connected_calendars_for_ui) return [];
            const q = this.calendarSearch.toLowerCase();
            return this.event.connected_calendars_for_ui.calendars.filter(c =>
                c.value.toLowerCase().includes(q) || c.key.toLowerCase().includes(q) ||
                this.calendarPrimaryLabel(c).toLowerCase().includes(q)
            );
        },
        selectedCalendarLabel() {
            if (!this.event.connected_calendars_for_ui) return '';
            const cal = this.event.connected_calendars_for_ui.calendars.find(c => c.key === this.selected_connected_calendar);
            if (!cal) return '';
            if (cal.type === 'None') return cal.value;
            return `${this.calendarPrimaryLabel(cal)} · ${cal.value}`;
        },
        selectedCalendarColor() {
            if (!this.event.connected_calendars_for_ui) return '#9ca3af';
            const cal = this.event.connected_calendars_for_ui.calendars.find(c => c.key === this.selected_connected_calendar);
            return cal?.color_hex || '#9ca3af';
        },
        selectedCalendarType() {
            if (!this.event.connected_calendars_for_ui) return null;
            const cal = this.event.connected_calendars_for_ui.calendars.find(c => c.key === this.selected_connected_calendar);
            return cal?.type || null;
        },
        hasRealCalendars() {
            return this.event.connected_calendars_for_ui?.calendars?.some(c => c.type !== 'None') || false;
        },
    },
    watch: {
        'event.name'(newValue) {
            if (!this.urlManuallyEdited) {
                this.event.url = this.slugify(newValue || '');
                this.checkUrlAvailability();
            }
        },
        'event.url'(newValue) {
            if (newValue !== null) {
                this.event.url = newValue.replace(/\s+/g, '-');
            }
        },
        'selected_connected_calendar'(newValue) {
            this.event.connected_calendar_id = newValue;
            delete this.errors.location;
        },
        'event.location.phone_call.selected_option'(newValue) {
            if (newValue === 'host_will_call_invitee') {
                this.event.location.phone_call.host_contact_number = '';
            }
        },
        'event.location.name'(newValue) {
            if (newValue !== null) {
                if (newValue === 'zoom') {
                    this.errors.location = this.selected_zoom === false
                        ? 'Connect Zoom to use this location'
                        : undefined;
                } else {
                    delete this.errors.location;
                }
            }
        },
    },
    mounted() {
        document.addEventListener('click', this.handleOutsideClick);
        this.$nextTick(() => {
            if (window.innerWidth >= 640) {
                this.$refs.eventNameInput?.focus();
            }
        });
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleOutsideClick);
    },
    async created() {
        if (this.current_event) {
            // server-side props (legacy ERB edit path, kept for compatibility)
            this.event = JSON.parse(this.current_event);
            this.slot_durations = JSON.parse(this.slot_durations_json);
            this.padding_durations = JSON.parse(this.padding_durations_to_json);
        } else if (this.$route?.params?.id) {
            // SPA edit: fetch form data for the existing event via API
            const res = await this.axiosRequest('GET', `/api/v1/events/${this.$route.params.id}/edit_form_data`, {}, false);
            if (res.js_error === false && res.response.success) {
                const d = res.response.data;
                this.event = d.current_event;
                this.slot_durations = d.slot_durations;
                this.padding_durations = d.padding_durations;
            }
        } else {
            // SPA new event
            const d = await useEventsStore().fetchNewFormData();
            this.event = d.current_event;
            this.slot_durations = d.slot_durations;
            this.padding_durations = d.padding_durations;
        }
        this.setDefaultLocation();
        this.selected_connected_calendar = this.event.connected_calendars_for_ui.selected_calendar.key;
        this.calendarSearch = this.selectedCalendarLabel;
        this.selected_zoom = this.event.connected_zoom_for_ui;
        this.user = await useGlobalStore().fetchCurrentUser();
        this.ready = true;
        this.$nextTick(() => {
            if (this.$refs.eventNameInput) this.$refs.eventNameInput.focus();
        });
    },
};
</script>

<style scoped>
.slide-enter-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.slide-leave-active { transition: opacity 0.1s ease, transform 0.1s ease; }
.slide-enter-from  { opacity: 0; transform: translateY(-4px); }
.slide-leave-to    { opacity: 0; transform: translateY(-4px); }

.details-enter-active { transition: opacity 0.18s ease; }
.details-leave-active { transition: opacity 0.12s ease; }
.details-enter-from, .details-leave-to { opacity: 0; }

.picker-enter-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.picker-leave-active { transition: opacity 0.1s ease, transform 0.1s ease; }
.picker-enter-from  { opacity: 0; transform: translateY(-4px); }
.picker-leave-to    { opacity: 0; transform: translateY(-4px); }
</style>
